import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/admin/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", website: "", public_phone: "", public_email: "", internal_referral_email: "", service_areas: "", referral_terms: "", internal_notes: "", public_profile: true, active: true };

export default function AdminAgencies() {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/admin/agencies").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openNew = () => setEdit({ ...EMPTY });
  const openEdit = (a) => setEdit({ ...a, service_areas: Array.isArray(a.service_areas) ? a.service_areas.join(", ") : (a.service_areas || "") });

  const save = async () => {
    if (!edit.name) { toast.error("Agency name is required"); return; }
    const payload = { ...edit, service_areas: edit.service_areas ? edit.service_areas.split(",").map((s) => s.trim()).filter(Boolean) : [] };
    try {
      if (edit.id) await api.put(`/admin/agencies/${edit.id}`, payload);
      else await api.post("/admin/agencies", payload);
      toast.success("Saved"); setEdit(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/agencies/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <PageHeader title="Agencies & partners" subtitle="Manage your property partners"
        action={<Button onClick={openNew} className="rounded-full bg-[#C81D35] hover:bg-[#a5162b] gap-2" data-testid="add-agency-btn"><Plus className="w-4 h-4" /> Add agency</Button>} />

      {items === null ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#C81D35] animate-spin" /></div>
        : items.length === 0 ? <div className="text-center py-16 bg-white rounded-xl border border-border text-muted-foreground" data-testid="agencies-empty">No agencies yet.</div>
        : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((a) => (
            <div key={a.id} className="bg-white rounded-xl border border-border p-5" data-testid={`agency-card-${a.id}`}>
              <div className="flex items-start justify-between">
                <div><h3 className="font-semibold text-[#111827]">{a.name}</h3><p className="text-xs text-muted-foreground">{a.public_phone}</p></div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`edit-agency-${a.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(a.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              {a.description && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{a.description}</p>}
              <div className="mt-3 flex gap-2 text-xs">
                <span className={`px-2 py-0.5 rounded-full ${a.active ? "bg-green-100 text-green-700" : "bg-gray-100"}`}>{a.active ? "Active" : "Inactive"}</span>
                {a.public_profile && <span className="px-2 py-0.5 rounded-full bg-muted">Public profile</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit agency" : "Add agency"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <Field label="Name *"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="agency-name" /></Field>
              <Field label="Description"><Textarea rows={2} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Website"><Input value={edit.website} onChange={(e) => setEdit({ ...edit, website: e.target.value })} /></Field>
                <Field label="Public phone"><Input value={edit.public_phone} onChange={(e) => setEdit({ ...edit, public_phone: e.target.value })} /></Field>
                <Field label="Public email"><Input value={edit.public_email} onChange={(e) => setEdit({ ...edit, public_email: e.target.value })} /></Field>
                <Field label="Internal referral email (private)"><Input value={edit.internal_referral_email} onChange={(e) => setEdit({ ...edit, internal_referral_email: e.target.value })} /></Field>
              </div>
              <Field label="Service areas (comma separated)"><Input value={edit.service_areas} onChange={(e) => setEdit({ ...edit, service_areas: e.target.value })} /></Field>
              <Field label="Referral terms (private)"><Input value={edit.referral_terms} onChange={(e) => setEdit({ ...edit, referral_terms: e.target.value })} /></Field>
              <Field label="Internal notes (private)"><Textarea rows={2} value={edit.internal_notes} onChange={(e) => setEdit({ ...edit, internal_notes: e.target.value })} /></Field>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm"><Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} /> Active</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={edit.public_profile} onCheckedChange={(v) => setEdit({ ...edit, public_profile: v })} /> Public profile</label>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)} className="rounded-full">Cancel</Button><Button onClick={save} className="rounded-full bg-[#C81D35] hover:bg-[#a5162b]" data-testid="save-agency">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Field({ label, children }) { return <div className="space-y-1"><Label className="text-sm">{label}</Label>{children}</div>; }
